import './LandingHelp.scss';

import { HelpCentre } from '@travelwin/cms/types';
import { Button, useConfig } from '@travelwin/core';
import { Trans, useTranslation } from 'react-i18next';

import { isRichTextEmpty } from '@/utils/lexicalUtilts';

import { ImageMedia } from '../../../components/ImageMedia';
import { RichText } from '../../../components/RichText';
import { useExternalHomepageConfig } from '../../../hooks/useExternalHomepageConfig';
import useLazyLoadImage from '../../../hooks/useLazyLoadImage';
import { useLocaleUtils } from '../../../hooks/useLocaleUtils';
import { getBrandSpecificShopImage } from '../../../utils';
import { isRemix } from '../../../utils/ssrUtils';
import { getPathnameWithLocale } from '../../../utils/urlUtils';

interface LandingHelpProps {
  helpCentre?: HelpCentre | null;
}

const getLocalizedUrlIfPossible = (url: string, currentLocale: string) =>
  url.startsWith('/') ? getPathnameWithLocale(url, currentLocale) : url;

const getProperUrlForHelpButton = (
  url: string | null | undefined,
  currentLocale: string,
  fallbackLink: string,
) => (url ? getLocalizedUrlIfPossible(url, currentLocale) : fallbackLink);

const className = 'p-LandingHelp';

const LandingHelp = ({ helpCentre }: LandingHelpProps) => {
  const { WHITELABEL_BRAND } = useConfig();
  const { t } = useTranslation();
  const { getHomepageUrl, getLocalizedZendeskUrl } =
    useExternalHomepageConfig();
  const { currentLocale } = useLocaleUtils();

  const helpImage = getBrandSpecificShopImage('help', WHITELABEL_BRAND);
  const imgRef = useLazyLoadImage(helpImage);

  const { title, helpText, buttons, helpCentreImage } = helpCentre || {};

  const { primaryBtnText, primaryBtnUrl, secondaryBtnText, secondaryBtnUrl } =
    buttons || {};

  return (
    <div className={`${className}__help-container`}>
      <div className={`${className}__help-image-container`}>
        {helpCentreImage ? (
          <ImageMedia
            media={helpCentreImage}
            className={`${className}__header-image`}
            sizes="(min-width: 768px) 50vw, 100vw"
            fetchpriority="high"
          />
        ) : (
          <img ref={imgRef} alt="" />
        )}
      </div>
      <div className={`${className}__help-text-container`}>
        <div className={`${className}__help-text-content`}>
          <div className={`${className}__help-text-title`}>
            {isRichTextEmpty(title) ? (
              t('landing.help.title', 'Need help?')
            ) : (
              <RichText content={title} />
            )}
          </div>
          <div className={`${className}__help-text-description`}>
            {isRichTextEmpty(helpText) ? (
              <Trans
                i18nKey="landing.help.description"
                defaults="Visit our Help Centre or contact us by phone, WhatsApp, or in person at our airport stores <a>Store Locator.</a>"
                components={{
                  a: (
                    <a
                      href={
                        isRemix()
                          ? getPathnameWithLocale(
                              '/store-locations',
                              currentLocale,
                            )
                          : getHomepageUrl('/store-locations')
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            ) : (
              <RichText content={helpText} />
            )}
          </div>
          <div className={`${className}__help-text-button`}>
            <Button
              onClick={() =>
                window.open(
                  getProperUrlForHelpButton(
                    primaryBtnUrl,
                    currentLocale,
                    getLocalizedZendeskUrl(),
                  ),
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              {primaryBtnText
                ? primaryBtnText
                : t('landing.help.button', 'Help Centre')}
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                window.open(
                  getProperUrlForHelpButton(
                    secondaryBtnUrl,
                    currentLocale,
                    isRemix()
                      ? getPathnameWithLocale('/contact-us', currentLocale)
                      : getHomepageUrl('/contact-us'),
                  ),
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              {secondaryBtnText
                ? secondaryBtnText
                : t('landing.help.support.button', 'Customer Support')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingHelp;
